import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, User, MapPin, Building2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import MultiImageUpload from "@/components/MultiImageUpload";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;
const REVIEW_TOKEN = 'george-secret-review-2025';

const SubmitReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const secretToken = searchParams.get('token');
  const isGuestAccess = !!secretToken;

  const [user, setUser] = useState(null);
  const [showJoinPopup, setShowJoinPopup] = useState(false);
  const [diveCenters, setDiveCenters] = useState([]);
  const [countries, setCountries] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [formData, setFormData] = useState({
    name: "", country: "", country_dove: "", country_dove_other: "",
    dive_center: "", dive_center_other: "", program: "", program_other: "",
    rating: 5, review_text: "", image_urls: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "null");

    if (currentUser) {
      setUser(currentUser);
      setFormData(prev => ({
        ...prev,
        name: currentUser.name || "",
        country: currentUser.country || ""
      }));
    } else if (isGuestAccess) {
      // Guest via secret link — show join popup
      setShowJoinPopup(true);
    } else {
      toast.error("Please login to submit a review");
      navigate('/account');
      return;
    }

    fetchDiveCenters();
  }, [navigate, isGuestAccess]);

  const fetchDiveCenters = async () => {
    try {
      const response = await axios.get(`${API}/map/dive-centers`);
      setDiveCenters(response.data);
      setCountries([...new Set(response.data.map(dc => dc.country))].sort());
      setPrograms([
        "Open Water Diver", "Advanced Open Water", "Rescue Diver",
        "Divemaster", "Fun Diving", "Discover Scuba Diving",
        "Specialty Course", "Refresher Course"
      ]);
    } catch (error) {
      console.error("Error fetching dive centers:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const actualCountryDove = formData.country_dove === "other" ? formData.country_dove_other : formData.country_dove;
    const actualDiveCenter = formData.dive_center === "other" ? formData.dive_center_other : formData.dive_center;
    const actualProgram = formData.program === "other" ? formData.program_other : formData.program;

    if (!formData.name || !formData.country || !actualCountryDove || !actualDiveCenter || !actualProgram || !formData.review_text) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        ...formData,
        country_dove: actualCountryDove,
        dive_center: actualDiveCenter,
        program: actualProgram,
        user_id: user?.id || null,
        image_urls: formData.image_urls,
        submission_token: secretToken || REVIEW_TOKEN
      });

      toast.success("Thank you! Your review will be published after approval.", { duration: 3000 });
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredDiveCenters = formData.country_dove && formData.country_dove !== "other"
    ? diveCenters.filter(dc => dc.country === formData.country_dove) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Join Popup Modal */}
      {showJoinPopup && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid="join-popup-overlay">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 space-y-6 text-center" data-testid="join-popup-choice">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Join Our Community</h2>
                <p className="text-sm text-gray-300 mt-2">Create an account to unlock exclusive features, track your dives, and connect with divers worldwide.</p>
              </div>

              <Button
                onClick={() => {
                  const returnPath = `/submit-review?token=${secretToken}`;
                  navigate(`/register?mode=review&token=${secretToken}&return=${encodeURIComponent(returnPath)}`);
                }}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-5 text-base"
                data-testid="join-yes-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Yes, sign me up!
              </Button>

              <button
                type="button"
                onClick={() => setShowJoinPopup(false)}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-200 py-2 transition-colors"
                data-testid="join-no-btn"
              >
                <Users className="w-4 h-4 inline mr-1" />
                No thanks, continue as guest
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="pt-32 pb-8 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Share Your Experience</h1>
          <p className="text-white/80">
            {user ? `Welcome, ${user.name}!` : "Tell us about your diving adventure"}
          </p>
        </div>
      </section>

      {/* Review Form — always visible */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Your Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-5 h-5 text-cyan-600" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Your Name *</Label>
                  <Input name="name" value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="John Doe" required disabled={!!user}
                    className={user ? "bg-gray-50" : ""} data-testid="review-name" />
                </div>
                <div className="space-y-2">
                  <Label>Your Country *</Label>
                  <Input name="country" value={formData.country}
                    onChange={(e) => setFormData(p => ({ ...p, country: e.target.value }))}
                    placeholder="e.g., Greece" required data-testid="review-country" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dive Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="w-5 h-5 text-cyan-600" />
                Dive Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country Where You Dove *</Label>
                  <Select value={formData.country_dove}
                    onValueChange={(v) => setFormData(p => ({
                      ...p, country_dove: v, dive_center: "", dive_center_other: "",
                      country_dove_other: v === "other" ? p.country_dove_other : ""
                    }))}>
                    <SelectTrigger data-testid="country-dove-select">
                      <SelectValue placeholder="Select country..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 bg-white">
                      {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="other" className="text-cyan-600 border-t mt-1">Other (not listed)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.country_dove === "other" && (
                    <Input name="country_dove_other" value={formData.country_dove_other}
                      onChange={(e) => setFormData(p => ({ ...p, country_dove_other: e.target.value }))}
                      placeholder="Enter country name" required className="mt-2" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Dive Center *</Label>
                  <Select value={formData.dive_center} disabled={!formData.country_dove}
                    onValueChange={(v) => setFormData(p => ({
                      ...p, dive_center: v,
                      dive_center_other: v === "other" ? p.dive_center_other : ""
                    }))}>
                    <SelectTrigger data-testid="dive-center-select">
                      <SelectValue placeholder={formData.country_dove ? "Select dive center..." : "Select country first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 bg-white">
                      {filteredDiveCenters.map(dc => <SelectItem key={dc.id} value={dc.name}>{dc.name}</SelectItem>)}
                      <SelectItem value="other" className="text-cyan-600 border-t mt-1">Other (not listed)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.dive_center === "other" && (
                    <Input name="dive_center_other" value={formData.dive_center_other}
                      onChange={(e) => setFormData(p => ({ ...p, dive_center_other: e.target.value }))}
                      placeholder="Enter dive center name" required className="mt-2" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Program & Rating */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="w-5 h-5 text-cyan-600" />
                Program & Rating
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Program/Activity *</Label>
                <Select value={formData.program}
                  onValueChange={(v) => setFormData(p => ({
                    ...p, program: v,
                    program_other: v === "other" ? p.program_other : ""
                  }))}>
                  <SelectTrigger data-testid="program-select">
                    <SelectValue placeholder="Select program or activity..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 bg-white">
                    {programs.map(pr => <SelectItem key={pr} value={pr}>{pr}</SelectItem>)}
                    <SelectItem value="other" className="text-cyan-600 border-t mt-1">Other (not listed)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.program === "other" && (
                  <Input name="program_other" value={formData.program_other}
                    onChange={(e) => setFormData(p => ({ ...p, program_other: e.target.value }))}
                    placeholder="Enter program or activity" required className="mt-2" />
                )}
              </div>

              <div className="space-y-2">
                <Label>Your Rating *</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button key={star} type="button"
                      onClick={() => setFormData(p => ({ ...p, rating: star }))}
                      className="transition-transform hover:scale-110" data-testid={`rating-star-${star}`}>
                      <Star className={`w-10 h-10 ${star <= formData.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300 hover:text-yellow-300"}`} />
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Review Text */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Star className="w-5 h-5 text-cyan-600" />
                Your Review
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Share your experience *</Label>
                <Textarea name="review_text" value={formData.review_text}
                  onChange={(e) => setFormData(p => ({ ...p, review_text: e.target.value }))}
                  placeholder="Share your diving experience..." rows={6} required
                  className="resize-none" data-testid="review-text" />
              </div>
              <MultiImageUpload
                images={formData.image_urls}
                onImagesChange={(urls) => setFormData(p => ({ ...p, image_urls: urls }))}
                uploadEndpoint="/api/upload/review-image"
                maxImages={5}
                label="Add Photos (Optional)"
              />
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <p className="text-cyan-800 text-sm">
                  <strong>Note:</strong> Your review will be reviewed and approved before appearing publicly.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={loading}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-lg py-6"
            data-testid="submit-review-btn">
            {loading ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      </div>

      <FloatingUserMenu />
      <Footer />
    </div>
  );
};

export default SubmitReview;
